import ContentstackLivePreview from '@contentstack/live-preview-utils';
import * as Utils from '@contentstack/utils';
import { addEditableTags } from '@contentstack/utils';
import { CachePolicy, Config, LivePreview, Region, Stack } from 'contentstack';
import {
  TFooterProps,
  TBannerProps,
  IOrNavBarBlockProps,
} from './types/layout';
import {
  IDesignStudiosJSON,
  ITmBlogCMSProps,
  TErrorTemplateCMS,
  TPage,
} from './types/pages';
import { ICustomUrls, IPromo } from './types';

import { notFound } from 'next/navigation';
import localDesignStudiosJSON from './utils/design_studios_3d.json';
import { AtText } from '@/components/atoms/at-text/at-text';
import { ILocationData } from '@/components/helpers/fetchInitialProps';

const liveEdit = process.env.CONTENTSTACK_LIVE_EDIT_TAGS === 'true';

type TGetEntriesWithFilter = {
  contentTypeUid: string;
  filters: Record<string, any> | undefined;
};

type TGetEntry = {
  contentTypeUid: string;
  referenceFieldPath: string[] | undefined;
  jsonRtePath: string[] | undefined;
};

type TGetEntryByUrl = {
  entryUrl: string | undefined;
  contentTypeUid: string;
  referenceFieldPath: string[] | undefined;
  jsonRtePath: string[] | undefined;
};

// Add paths to reference blocks to resolve the relations (referenceFieldPath)
export const resolveRelations = [
  'blog_landing_header',
  'blog_post',
  'body',
  'body.left_content',
  'body.left_content.link',
  'body.testimonial_cards.cta',
  'body.right_content.link.icon',
  'body.stats',
  'body.right_content',
  'body.cards',
  'body.cards.cta',
  'body.cards.icon',
  'body.cards.chip',
  'body.cards.chip.icon',
  'body.plans',
  'body.heading',
  'body.heading.cta',
  'body.promo_block_copy',
  'body.main_card',
  'body.complementary_cards',
  'body.cta.icon',
  'body.copy_block_content.link.icon',
  'body.copy_content.link.icon',
  'body.lead_gen_form_design_studio',
  'body.lead_gen_form',
  'body.body.lead_gen_form',
  'body.cta_button_top.icon',
  'body.cta_button_bottom.icon',

  // Templates
  'body.faq',
  'body.design_center_info.card.icon',
  'body.awards_banner.icon',
  'body.tour_banner.icon',
  'body.body',
  'body.body.stats',
  'body.body.left_content',
  'body.body.left_content.link',
  'body.body.right_content',
  'body.body.right_content.link.icon',
  'body.related.blog',
  'body.exterior_switcher',
  'body.body.testimonial_cards.cta',
  'body.body.cards',
  'body.body.cards.cta',
  'body.body.cards.icon',
  'body.body.cards.chip',
  'body.body.plans',
  'body.body.heading',
  'body.body.heading.cta',
  'body.body.promo_block_copy',
  'body.body.main_card',
  'body.body.complementary_cards',
  'body.whats_included_section',
  'body.body.lead_gen_form',
];

/**
 * Converts a Contentstack API URL into a CDN URL.
 * Example: 'https://api.contentstack.io' → 'https://cdn.contentstack.io'
 */
export const customHostUrl = (baseUrl: string): string => {
  return baseUrl.replace('api', 'cdn');
};

/**
 * Validates whether the provided custom host URL matches the current region.
 * @param url - The URL to validate
 * @returns `true` if the URL is valid and custom, `false` if it's a standard region URL
 */
export const isValidCustomHostUrl = (url = ''): boolean => {
  return url ? !generateUrlBasedOnRegion().includes(url) : false;
};

const customHostBaseUrl = process.env.CONTENTSTACK_API_HOST
  ? customHostUrl(process.env.CONTENTSTACK_API_HOST)
  : '';

/**
 * Resolves the Contentstack region to be used by the SDK,
 * based on the CONTENTSTACK_REGION environment variable.
 */
const setRegion = (): Region => {
  let region = 'US' as keyof typeof Region;
  if (
    !!process.env.CONTENTSTACK_REGION &&
    process.env.CONTENTSTACK_REGION !== 'us'
  ) {
    region = process.env.CONTENTSTACK_REGION.toLocaleUpperCase().replace(
      '-',
      '_',
    ) as keyof typeof Region;
  }
  return Region[region];
};

/**
 * Builds the Live Preview configuration object for the SDK.
 * Throws an error if required LP env vars are missing.
 */
const setLivePreviewConfig = (): LivePreview => {
  if (!isLpConfigValid())
    throw new Error(
      'Your LP config is set to true. Please make you have set all required LP config in .env',
    );
  return {
    preview_token: process.env.CONTENTSTACK_PREVIEW_TOKEN as string,
    enable: process.env.CONTENTSTACK_LIVE_PREVIEW === 'true',
    host: process.env.CONTENTSTACK_PREVIEW_HOST as string,
  } as LivePreview;
};

/**
 * Returns a list of valid production CDN URLs for each supported region.
 */
export const generateUrlBasedOnRegion = (): string[] => {
  return Object.keys(Region).map((region) => {
    if (region === 'US') {
      return `cdn.contentstack.io`;
    }
    return `${region}-cdn.contentstack.com`;
  });
};

/**
 * Validates that required base environment variables are set.
 * These are required to initialize the SDK.
 */
export const isBasicConfigValid = () => {
  return (
    !!process.env.CONTENTSTACK_API_KEY &&
    !!process.env.CONTENTSTACK_DELIVERY_TOKEN &&
    !!process.env.CONTENTSTACK_ENVIRONMENT
  );
};

/**
 * Validates that all required environment variables for Live Preview are present.
 */
export const isLpConfigValid = () => {
  return (
    !!process.env.CONTENTSTACK_LIVE_PREVIEW &&
    !!process.env.CONTENTSTACK_PREVIEW_TOKEN &&
    !!process.env.CONTENTSTACK_PREVIEW_HOST &&
    !!process.env.CONTENTSTACK_APP_HOST
  );
};

/**
 * Initializes and returns a configured Contentstack SDK instance.
 * Will throw if required env vars are missing.
 */
export const initializeContentStackSdk = (): Stack => {
  if (!isBasicConfigValid())
    throw new Error('Please set your .env file before running the app');

  const stackConfig: Config = {
    api_key: process.env.CONTENTSTACK_API_KEY as string,
    delivery_token: process.env.CONTENTSTACK_DELIVERY_TOKEN as string,
    environment: process.env.CONTENTSTACK_ENVIRONMENT as string,
    region: setRegion(),
    branch: process.env.CONTENTSTACK_BRANCH || 'main',
  };
  if (process.env.CONTENTSTACK_LIVE_PREVIEW === 'true') {
    stackConfig.live_preview = setLivePreviewConfig();
  }
  return Stack(stackConfig);
};

// SDK initialization
let CStack: Stack;

if (process.env.STORYBOOK !== 'true') {
  // Normal app / Next.js build
  CStack = initializeContentStackSdk();

  if (!!customHostBaseUrl && isValidCustomHostUrl(customHostBaseUrl)) {
    CStack.setHost(customHostBaseUrl);
  }

  CStack.setCachePolicy(CachePolicy.CACHE_THEN_NETWORK);

  if (
    process.env.CONTENTSTACK_LIVE_PREVIEW === 'true' &&
    process.env.NODE_ENV !== 'production'
  ) {
    ContentstackLivePreview.init({
      stackSdk: CStack,
      clientUrlParams: {
        host: process.env.CONTENTSTACK_API_HOST,
      },
      ssr: false,
    })?.catch((err) => console.error(err));
  }
} else {
  console.warn(
    'Storybook build detected — Contentstack SDK initialization skipped.',
  );
}

export const { onEntryChange } = ContentstackLivePreview;
export const contentStackLivePreview = ContentstackLivePreview;

const renderOption = {
  span: (node: any, next: any) => next(node.children),
  p: (node: any, next: any) => `<${AtText}>${next(node.children)}</${AtText}>`,
  bold: (text: string) => `<strong>${text}</strong>`,
  default: (node: any, next: any) => `<div>${next(node.children)}</div>`,
};

// Experiment to try to avoid repeated hits to content stack for urls that don't exist
const badUrlCache = new Set<string>();

/**
 * Fetches a page entry from Contentstack based on a dynamic slug.
 *
 * Supports both static and dynamic routing (e.g., \`/about-us\`, \`['plans', 'ranch']\`, etc.)
 * Handles Live Preview fallback logic in the browser.
 *
 * @param {slug} - A string or string[] representing the URL path
 * @param {isMetaFetch} - If true, skips client-side checks and returns the entry for metadata
 * @returns A Promise resolving to the matching \`TPage\` entry, or undefined
 */
export async function fetchEntry(
  slug: string[] | string,
  isMetaFetch: boolean = false,
  contentTypeUID?: string,
) {
  try {
    // Ensure slug is a string (or array if you expect it), as useRouter might initially have it as undefined
    const entrySlug = Array.isArray(slug) ? slug.join('/') : slug || '/';

    if (!entrySlug) return; // Guard clause if slug is not yet defined
    const entryUrl = entrySlug.startsWith('/') ? entrySlug : `/${entrySlug}`;

    if (badUrlCache.has(entryUrl)) notFound();

    const entryRes = await getPageRes(entryUrl, contentTypeUID);

    // When fetching metadata, we are doing this before browser is initialized fully, because of the below check,
    // metadata is failing to populate for SEO. From the getMetadata fn we are passing in this flag, to ensure the entryRes is sent
    if (isMetaFetch) {
      //console.debug('Fetched metadata ', entryUrl, contentTypeUID, entryRes);
      return entryRes;
    }

    // Noticed in preview mode, if a page isn't published we get a 404 error.
    // This checks to see if windowType === 'preview' and if so, return the entryResponse; otherwise return the expected 404
    if (typeof window !== 'undefined') {
      const { windowType } = ContentstackLivePreview?.config;
      if (windowType === 'preview') {
        return entryRes;
      }

      if (entryRes) {
        return entryRes;
      }

      badUrlCache.add(entryUrl);
      notFound();
    }

    return entryRes;
  } catch (error) {
    console.error(
      'Failed to fetch content: ',
      slug,
      isMetaFetch,
      contentTypeUID,
      error,
    );
  }
}

/**
 * Fetches the CMS entry for the global banner.
 */
export const getBannerRes = async (): Promise<TBannerProps> => {
  const response = (await getEntry({
    contentTypeUid: 'global_banner',
    referenceFieldPath: [],
    jsonRtePath: [],
  })) as TBannerProps[][];

  liveEdit && addEditableTags(response[0][0], 'global_banner', true);
  return response[0][0];
};

/**
 * Fetches the CMS entry for the global ABA form.
 */
export const getAbaFormRes = async (): Promise<any> => {
  const response = (await getEntry({
    contentTypeUid: 'aba_form',
    referenceFieldPath: [],
    jsonRtePath: [],
  })) as TBannerProps[][];

  liveEdit && addEditableTags(response[0][0], 'aba_form', true);
  return response[0][0];
};

/**
 * Fetches asset entries from Contentstack that belong to a specific folder UID.
 *
 * Adds dimension data to the response (e.g., width/height) and returns up to 2000 assets.
 *
 * @param {folderUid} - The UID of the asset folder to filter by
 * @returns A Promise resolving to an array of asset objects belonging to that folder
 */
export const getGalleryAssetsOld = async (folderUid: string) => {
  const allAssets: any[] = [];
  const pageSize = 250;
  let skip = 0;
  let hasMore = true;

  try {
    while (hasMore) {
      const query = CStack.Assets()
        .Query()
        .addParam('include_dimension', 'true')
        .where('parent_uid', folderUid)
        .limit(pageSize)
        .skip(skip);

      const response = await query.toJSON().find();
      const assets = response?.[0] || [];

      allAssets.push(...assets);

      if (assets.length < pageSize) {
        hasMore = false;
      } else {
        skip += pageSize;
      }
    }

    return allAssets;
  } catch (err) {
    console.error('Error fetching assets by folder:', err);
    return [];
  }
};

/**
 * Fetches the CMS content for an error page (e.g., 404 or 500).
 *
 * Looks for an entry in the \`tm_error\` content type with a title like "TM Error: 404".
 *
 * @param {errorCode} - The HTTP error code ('404' or '500')
 * @returns A Promise resolving to the corresponding error page entry, or \`undefined\` if not found
 */
export const getErrorPageProps = async (
  errorCode: '404' | '500',
): Promise<TErrorTemplateCMS | undefined> => {
  try {
    const query = CStack.ContentType('tm_error').Query();

    const response = await query
      .query({ title: `TM Error: ${errorCode}` })
      .toJSON()
      .find();

    if (response[0] && response[0][0]) {
      return response[0][0];
    }

    return undefined;
  } catch (e) {
    console.error('Failed to fetch error page content for ', errorCode, e);
    return undefined;
  }
};

/**
 * Fetches external location data defined in the \`locations\` content type.
 *
 * This entry contains a URL to a hosted JSON file with the actual location data.
 * The JSON file is then fetched and returned as parsed data.
 *
 * @returns A Promise resolving to an array of location data objects
 */
export const getLocationsRes = async (): Promise<ILocationData[]> => {
  const response = (await getEntry({
    contentTypeUid: 'locations',
    referenceFieldPath: [],
    jsonRtePath: [],
  })) as any;

  try {
    const url = response[0][0]?.location_data.url;

    // fetch the actual json file
    const data = await fetch(url);

    return await data.json();
  } catch (e) {
    console.error('Failed to fetch location data ', e);
    return [];
  }
};

/**
 * Fetches the CMS entry for the global header/nav bar.
 */
export const getHeaderRes = async (): Promise<IOrNavBarBlockProps> => {
  const response = (await getEntry({
    contentTypeUid: 'global_main_nav',
    referenceFieldPath: [
      'link_list.nav_item.link_card',
      'nav_footer_content.chat_block.icon',
      'nav_footer_content.chat_block.bottom_cta',
    ],
    jsonRtePath: [],
  })) as IOrNavBarBlockProps[][];

  liveEdit && addEditableTags(response[0][0], 'global_main_nav', true);
  return response[0][0];
};

/**
 * Fetches all custom URL entries from Contentstack.
 */
export const getGlobalLinksRes = async (): Promise<ICustomUrls[]> => {
  const res = await getEntriesWithFilter({
    contentTypeUid: 'custom_urls',
    filters: {},
  });
  return (await res) as ICustomUrls[];
};

/**
 * Fetches the CMS entry for the global site footer.
 */
export const getFooterRes = async (): Promise<TFooterProps> => {
  const response = (await getEntry({
    contentTypeUid: 'footer',
    referenceFieldPath: [
      'footer_action_column.cta_1',
      'footer_action_column.social_links.links',
      'footer_action_column.social_links.links.icon',
      'footer_action_column.cta_2',
      'footer_action_column.auth_cta',
      'footer_nav_column.link',
      'legal_links.links',
    ],
    jsonRtePath: ['copyright'],
  })) as TFooterProps[][];

  liveEdit && addEditableTags(response[0][0], 'footer', true);

  return response[0][0];
};

/**
 * Fetches all entries of type 'page', resolving all defined reference fields.
 */
export const getAllEntries = async (): Promise<TPage[]> => {
  const response = (await getEntry({
    contentTypeUid: 'page',
    referenceFieldPath: resolveRelations,
    jsonRtePath: undefined,
  })) as TPage[][];
  liveEdit &&
    response[0].forEach((entry) => addEditableTags(entry, 'page', true));

  return response[0];
};

/**
 * Fetches a single page by its URL and resolves all reference fields.
 * @param {entryUrl} - The URL path of the page (e.g. '/floor-plans')
 */
export async function getPageRes(entryUrl: string, contentTypeUID?: string) {
  const response = (await getEntryByUrl({
    contentTypeUid: contentTypeUID ?? 'page',
    entryUrl,
    referenceFieldPath: resolveRelations,
    jsonRtePath: [],
  })) as TPage[];

  liveEdit && addEditableTags(response[0], 'page', true);
  return response[0];
}

/**
 * Fetches all entries of type 'promo'.
 */
export const getPromos = async (options?: {}): Promise<IPromo[]> => {
  const response = (await getEntriesWithFilter({
    contentTypeUid: 'promo',
    filters: {},
  })) as IPromo[];
  return response;
};
/**
 * Fetches entries from a specific content type in Contentstack with applied filters.
 *
 * @param {string} contentTypeUid - The UID of the content type to fetch entries from.
 * @param {Object} filters - Key-value pairs for filtering results.
 *        - Supports exact matches: `{ key: "value" }`
 */

export const getEntriesWithFilter = ({
  contentTypeUid,
  filters,
}: TGetEntriesWithFilter) => {
  return new Promise((resolve, reject) => {
    try {
      const query = CStack.ContentType(contentTypeUid).Query();

      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          query.where(key, value);
        });
      }

      query
        .toJSON()
        .find()
        .then(
          (result) => resolve(result[0] || []),
          (error) => reject(error),
        );
    } catch (e) {
      console.error('Failed get content with filters ', contentTypeUid, e);
      reject(e);
    }
  });
};

/**
 *
 * fetches all the entries from specific content-type
 * @param {content-type uid} contentTypeUid
 * @param {reference field name} referenceFieldPath
 * @param {Json RTE path} jsonRtePath
 *
 */
export async function getEntry({
  contentTypeUid,
  referenceFieldPath,
  jsonRtePath,
}: TGetEntry) {
  try {
    const query = CStack.ContentType(contentTypeUid).Query().limit(2000);
    if (referenceFieldPath) query.includeReference(referenceFieldPath);
    const result = await query.toJSON().find();

    jsonRtePath &&
      Utils.jsonToHTML({
        entry: result,
        paths: jsonRtePath,
        renderOption,
      });

    return result;
  } catch (e) {
    console.error('Failed getEntry ', contentTypeUid, e);
    return {};
  }
}

/**
 *
 * fetches the n most recent blog_post content types
 */
export function getTopBlogPosts() {
  return new Promise<ITmBlogCMSProps[][]>((resolve, reject) => {
    const query = CStack.ContentType('blog_post')
      .Query()
      .descending('updated_at') // Sort by 'updated_at' in descending order (most recent first)
      .limit(10);
    //if (referenceFieldPath) query.includeReference(referenceFieldPath);
    query
      .toJSON()
      .find()
      .then(
        (result) => {
          resolve(result as ITmBlogCMSProps[][]);
        },
        (error) => {
          reject(error);
        },
      );
  });
}

const BLOG_POST_RESOLVE_RELATIONS = [
  'hero_image.mobile_image_url',
  'content_body.cards.card.link',
  'faq',
];

/**
 *
 * Fetches all blog_post content types with full nested data supporting infinite scroll pagination via limit() & skip()
 */
export const getAllBlogPosts = async () => {
  const result = await CStack.ContentType('blog_post')
    .Query()
    .includeCount()
    .limit(12)
    .skip(0)
    .descending('date')
    .includeReference(BLOG_POST_RESOLVE_RELATIONS)
    .includeFallback()
    .toJSON()
    .find();

  return result; // Returns [posts, count]
};

// Helper for client-side fetches
export const getMoreBlogPosts = async ({
  skip,
  limit,
}: {
  skip: number;
  limit: number;
}) => {
  const result = await CStack.ContentType('blog_post')
    .Query()
    .limit(limit)
    .skip(skip)
    .descending('date')
    .includeReference(BLOG_POST_RESOLVE_RELATIONS)
    .includeFallback()
    .toJSON()
    .find();

  return result; // Returns [posts, count] (only using posts)
};

/**
 *fetches specific entry from a content-type
 *
 * @param {content-type uid} contentTypeUid
 * @param {url for entry to be fetched} entryUrl
 * @param {reference field name} referenceFieldPath
 * @param {Json RTE path} jsonRtePath
 * @returns
 */
export async function getEntryByUrl({
  contentTypeUid,
  entryUrl,
  referenceFieldPath,
  jsonRtePath,
}: TGetEntryByUrl) {
  try {
    const blogQuery = CStack.ContentType(contentTypeUid).Query();
    if (referenceFieldPath) blogQuery.includeReference(referenceFieldPath);
    blogQuery.includeEmbeddedItems();
    blogQuery.toJSON();
    const result = await blogQuery.where('url', `${entryUrl}`).find();

    //console.debug(jsonRtePath, result);

    jsonRtePath &&
      Utils.jsonToHTML({
        entry: result,
        paths: jsonRtePath,
        renderOption,
      });

    //console.debug(result);

    return result?.length ? result[0] : [];
  } catch (e) {
    console.error('Failed getEntryByUrl ', contentTypeUid, entryUrl, e);
    return {};
  }
}

/**
 * Fetches the design studios JSON data from a remote asset in Contentstack.
 *
 * Uses a different asset ID depending on the current branch (`main` vs others).
 * If the remote fetch fails (e.g., in local/dev or due to network issues), it falls back to a local JSON file.
 *
 * @returns A Promise resolving to the parsed \`IDesignStudiosJSON\` object
 */
export const getDesignStudiosDataFromJSON =
  async (): Promise<IDesignStudiosJSON> => {
    try {
      const assetId =
        process.env.NEXT_PUBLIC_3DJSON_UID || 'blt32e91c4fd8e52c7a';

      const result = await CStack.Assets(assetId).toJSON().fetch();
      const json: IDesignStudiosJSON = await fetch(result.url, {
        headers: { 'content-type': 'application/json' },
      }).then((r) => r.json());
      return json;
    } catch (e) {
      // If fetching the remote JSON file fails, we return the local version of it as a fallback.
      console.error(
        'Failed to get design studio 3D file -- providing outdated fallback',
        e,
      );
      return localDesignStudiosJSON;
    }
  };

/**
 * Checks whether a given design studio ID supports 3D functionality.
 *
 * Attempts to fetch a remote JSON file containing the full list of studios and their 3D support.
 * Falls back to a local version of the file if remote fetch fails.
 *
 * @param {designStudioId} - The numeric ID of the design studio to check
 * @returns A Promise resolving to true if the studio supports 3D, false otherwise
 */
export const doesDesignStudioSupport3D = async (
  designStudioId: number,
  designStudiosJSON: IDesignStudiosJSON,
): Promise<boolean> => {
  const designStudio = designStudiosJSON.design_studios?.find(
    (ds) => ds.id === designStudioId,
  );

  return designStudio ? designStudio.has3D : false;
};

/**
 * Retrieves the Calendly link (if any) for a given design studio ID.
 *
 * Uses the same design studio JSON used in \`doesDesignStudioSupport3D()\`.
 *
 * @param {designStudioId} - The numeric ID of the design studio
 * @returns A Promise resolving to the Calendly link string or \`undefined\` if not found
 */
export const getDesignStudioCalendlyLink = async (
  designStudioId: number,
  designStudiosJSON: IDesignStudiosJSON,
): Promise<string | undefined> => {
  const designStudio = designStudiosJSON?.design_studios.find(
    (ds) => ds.id === designStudioId,
  );

  return designStudio ? designStudio.calendlyLink : undefined;
};
